import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import type { CompiledFormViewConfig, ViewUIChildRecordsForm } from '@salescore/core'
import { Avatar, Badge, Button, Col, Collapse, Empty, Row, Space } from 'antd'
import { t } from 'i18next'
import { type ReactElement, useState } from 'react'

import { VIEW_NEW_RECORD_PREFIX } from '../../domain/constant'
import {
  type RecordNodePath,
  useSetUpsertViewRecordsByPath,
} from '../../recoil/records/mutations/upsertViewRecordsByPathMutation'
import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'
import { useViewUIRecordNodeContext, ViewUIRecordNodeContext, ViewUIRecordNodeContextClass } from './contexts'
import { ModelIcon } from './ModelIcon'
import { ViewUIFormComponentC } from './ViewUIComponent'

export const ViewUIChildRecordsFormC = ({
  component,
  compiledConfig,
  showError,
  view,
  ability,
}: {
  component: ViewUIChildRecordsForm
  compiledConfig: CompiledFormViewConfig
  showError: boolean
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { recordNodePath, recordNode } = useViewUIRecordNodeContext()
  const childTableNode = recordNode?.children.find((x) => x.nodeName === component.tableNodeName)
  const upsertViewRecordsByPath = useSetUpsertViewRecordsByPath(compiledConfig, ability)
  const [collapsedKeys, setCollapsedKeys] = useState<string[]>([])

  if (childTableNode === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO
    return <></>
  }

  return (
    <Row className="m-0">
      <Collapse
        defaultActiveKey={[]}
        className="mb-4 w-full"
        onChange={(keys) => {
          setCollapsedKeys(keys)
        }}
      >
        <Collapse.Panel
          key="items"
          className="p-0"
          // collapsible={childTableNode.children.length === 0 ? 'disabled' : undefined}
          header={
            <Row justify="space-between" className="w-full">
              <Space className="font-bold">
                <Avatar
                  style={{ backgroundColor: component.color ?? '#888' }}
                  size={28}
                  shape={'square'}
                  icon={<ModelIcon icon={component.icon} fontSize={16} />}
                />
                {component.label}
                {component.relation === 'one_to_many' && (
                  <>
                    <Badge count={childTableNode.children.length} style={{ backgroundColor: '#aaa' }} />
                  </>
                )}
              </Space>
              <Space>
                {component.relation === 'one_to_many' && collapsedKeys.length > 0 && (
                  <>
                    {/* eslint-disable-next-line sonarjs/todo-tag */}
                    {/* TODO: expandしているときだけ表示 */}
                    <Button
                      onClick={(e) => {
                        upsertViewRecordsByPath({
                          newRecordChanges: [
                            {
                              recordNodePath,
                              tableNodeName: childTableNode.nodeName,
                              insertIndex: 0,
                            },
                          ],
                        })
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      // type="text"
                      icon={<PlusOutlined />}
                      className="text-blue-600"
                    >
                      {t(`追加`)}
                    </Button>
                  </>
                )}
              </Space>
            </Row>
          }
        >
          <div>
            {childTableNode.children.length === 0 && <Empty />}
            {childTableNode.children.map((childRecordNode, index) => {
              // eslint-disable-next-line sonarjs/todo-tag
              const isNewRecord = childRecordNode.id?.startsWith(VIEW_NEW_RECORD_PREFIX) ?? false // TODO: どこかのメタ属性で持つか？
              const nameValue = isNewRecord
                ? t(`新しいレコード`)
                : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/todo-tag
                  (childRecordNode.attributes[component.nameFieldName ?? ''] as string | undefined) // TODO
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/todo-tag
              const urlValue = childRecordNode.attributes[component.recordUrlFieldName ?? ''] as string | undefined // TODO
              const childRecordNodePath: RecordNodePath = [
                ...recordNodePath,
                {
                  tableNodeName: childTableNode.nodeName,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion,sonarjs/todo-tag
                  recordNodeId: childRecordNode.id!, // TODO
                },
              ]
              return (
                <div
                  key={`childrecordnode-${childRecordNode.id ?? index}`}
                  className="mb-4 p-0 px-2 pb-2"
                  style={{
                    borderBottom: `1px solid #DADADA`,
                  }}
                >
                  {/* シートのカスタム列を定義したときに、name要素と削除を表示する必要がなかったので、以下ロジックをいれている。
                    現状one_to_oneになるのはシートのカスタム列だけなので、雑にone_to_oneを指定してしまっているが、他でもone_to_oneを使うことになった際に、この条件で正しいかはやや微妙かも
                    one_to_oneがが使われるユースケースを正確に全部把握できていないが、一旦この形とする（これで悪くはないはず）。
                   */}
                  {component.relation !== 'one_to_one' && (
                    <Row className="mb-2" justify="space-between">
                      <Col>
                        <Space
                          style={{
                            fontSize: 16,
                          }}
                        >
                          <Avatar
                            style={{ backgroundColor: component.color ?? '#888' }}
                            size={24}
                            shape={'square'}
                            icon={<ModelIcon icon={component.icon} fontSize={14} />}
                          />
                          {urlValue === undefined ? (
                            <span>{nameValue ?? childRecordNode.id ?? ''}</span>
                          ) : (
                            <a target="_blank" href={urlValue} rel="noreferrer">
                              {nameValue ?? childRecordNode.id ?? ''}
                            </a>
                          )}
                        </Space>
                      </Col>
                      <Col>
                        <Space>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                              upsertViewRecordsByPath({
                                deleteRecordChanges: [
                                  {
                                    recordNodePath: childRecordNodePath,
                                    // eslint-disable-next-line sonarjs/todo-tag
                                    // isDeleteAllChildren: true // TODO: 全て削除のUI
                                  },
                                ],
                              })
                              e.preventDefault()
                              e.stopPropagation()
                            }}
                          >
                            {t(`削除`)}
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  )}
                  <ViewUIRecordNodeContext.Provider
                    value={
                      new ViewUIRecordNodeContextClass({
                        recordNode: childRecordNode,
                        recordNodePath: childRecordNodePath,
                        upsertViewRecordsByPath,
                      })
                    }
                  >
                    {component.children.map((child, childIndex) => (
                      <ViewUIFormComponentC
                        key={`viewuiformcomponentc-child-${childIndex}`}
                        component={child}
                        compiledConfig={compiledConfig}
                        showError={showError}
                        view={view}
                        ability={ability}
                      />
                    ))}
                  </ViewUIRecordNodeContext.Provider>
                </div>
              )
            })}
            {/* <div>
              <Button icon={<PlusOutlined />}>追加</Button>
            </div> */}
          </div>
        </Collapse.Panel>
      </Collapse>
    </Row>
  )
}
